
import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LegacyCity, LegacyJob } from '../types';
import { getJobs } from '../src/api';
import JobRow from './JobRow';

// 使用 Legacy 类型用于向后兼容
type Job = LegacyJob;

interface CityDetailProps {
  city: LegacyCity;
  onBack: () => void;
  onBackToHome: () => void;
  onSelectJob: (job: Job) => void;
}

const CityDetail: React.FC<CityDetailProps> = ({ city, onBack, onBackToHome, onSelectJob }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [departmentFilter, setDepartmentFilter] = useState<string>('All');
  const [typeFilter, setTypeFilter] = useState<string>('All');

  // 从 API 获取城市职位
  const [allCityJobs, setAllCityJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCityJobs = async () => {
      try {
        // 使用 city 参数从 API 获取该城市的职位
        const jobs = await getJobs({ city: city.id });
        setAllCityJobs(jobs);
      } catch (error) {
        console.error('Failed to fetch city jobs:', error);
        setAllCityJobs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCityJobs();
  }, [city.id]);

  // 获取城市名称（根据语言）
  const getCityName = () => currentLang.startsWith('zh') ? city.name : city.enName;

  // 获取国家翻译
  const getCountryName = () => {
    const countryMap: Record<string, { zh: string; en: string }> = {
      'China': { zh: '中国', en: 'China' },
      'UK': { zh: '英国', en: 'UK' },
      'USA': { zh: '美国', en: 'USA' },
      'India': { zh: '印度', en: 'India' },
      'Vietnam': { zh: '越南', en: 'Vietnam' },
      'Malaysia': { zh: '马来西亚', en: 'Malaysia' },
      'Singapore': { zh: '新加坡', en: 'Singapore' },
      'Thailand': { zh: '泰国', en: 'Thailand' },
      'Indonesia': { zh: '印度尼西亚', en: 'Indonesia' },
      'South Korea': { zh: '韩国', en: 'South Korea' },
    };
    const country = countryMap[city.country];
    if (!country) return city.country;
    return currentLang.startsWith('zh') ? country.zh : country.en;
  };

  // 翻译部门名称
  const getDepartmentLabel = (dept: string) => {
    if (dept === 'All') return t('cityDetail.filterAll');
    const key = dept.toLowerCase().replace(/\s+/g, '');
    return t(`jobData.departments.${key}`, dept);
  };

  // 翻译工作类型
  const getJobTypeLabel = (type: string) => {
    if (type === 'All') return t('cityDetail.filterAll');
    const typeMap: Record<string, string> = {
      'FULL_TIME': 'fullTime',
      'PART_TIME': 'partTime',
      'INTERNSHIP_OFFLINE': 'internshipOffline',
      'REMOTE': 'remote'
    };
    const key = typeMap[type] || 'fullTime';
    return t(`jobData.jobTypes.${key}`, type);
  };

  // 根据筛选条件过滤职位，并按优先级排序（紧急的排在前面）
  const cityJobs = useMemo(() => {
    const filtered = allCityJobs.filter(job => {
      const matchesDept = departmentFilter === 'All' || job.department === departmentFilter;
      const matchesType = typeFilter === 'All' || job.type === typeFilter;
      return matchesDept && matchesType;
    });
    
    // 按优先级排序：high > medium > low
    const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return filtered.sort((a, b) => {
      const aPriority = priorityOrder[a.priority || 'medium'] ?? 1;
      const bPriority = priorityOrder[b.priority || 'medium'] ?? 1;
      return aPriority - bPriority;
    });
  }, [allCityJobs, departmentFilter, typeFilter]);

  // 从已加载的职位中提取部门列表
  const departments = useMemo(() => {
    const depts = new Set(allCityJobs.map(j => j.department));
    return ['All', ...Array.from(depts)];
  }, [allCityJobs]);
  const types = ['All', 'FULL_TIME', 'INTERNSHIP_OFFLINE', 'REMOTE'];

  // 检查是否为远程城市
  const isRemoteHub = city.isRemoteOnly ?? false;

  return (
    <div className={`pt-20 sm:pt-24 pb-16 sm:pb-24 min-h-screen ${isRemoteHub ? 'bg-gradient-to-br from-green-50 to-blue-50' : 'bg-slate-50'}`}>
      {/* ① Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6 sm:mb-8">
        <nav className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium overflow-x-auto scrollbar-hide">
          <Link
            to="/"
            className="text-slate-400 hover:text-blue-600 transition-colors shrink-0 cursor-pointer"
          >
            {t('cityDetail.breadcrumbHome')}
          </Link>
          <span className="text-slate-300 shrink-0">/</span>
          <span className="text-slate-900 font-semibold">{getCityName()}</span>
        </nav>
      </div>

      {/* ② City Hero Area */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 sm:mb-12">
        <div className={`relative h-[250px] sm:h-[320px] md:h-[400px] rounded-xl overflow-hidden shadow-2xl ${isRemoteHub ? 'border-4 border-green-200' : ''}`}>
          {!isRemoteHub ? (
            // 原有的城市图片展示
            <>
              <img
                src={city.image}
                alt={city.name}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
            </>
          ) : (
            // Remote Hub 展示模式
            <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-blue-600 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="mb-3 sm:mb-6">
                  <svg className="w-12 h-12 sm:w-20 sm:h-20 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-2 sm:mb-4">{t('cityDetail.remoteHub')}</h2>
                <p className="text-sm sm:text-xl font-medium opacity-90">{t('cityDetail.remoteHubSubtitle')}</p>
              </div>
            </div>
          )}

          <div className={`absolute bottom-4 sm:bottom-8 md:bottom-12 left-4 sm:left-8 md:left-12 right-4 sm:right-8 md:right-12 ${isRemoteHub ? 'text-white' : 'text-white'}`}>
            {!isRemoteHub && (
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
                <span className="text-blue-300 font-bold uppercase tracking-widest text-[10px] sm:text-xs">
                  {getCountryName()} · {getCityName()}
                </span>
              </div>
            )}

            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-2 sm:mb-6 tracking-tight">
              {city.name} {!isRemoteHub && <span className="text-slate-300 font-light text-xl sm:text-3xl md:text-4xl">{t('cityDetail.careers')}</span>}
            </h1>

            {isRemoteHub && (
              <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border-2 border-white/30 mb-2 sm:mb-4">
                <span className="w-4 h-4 sm:w-5 sm:h-5">
                  <svg className="w-full h-full text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2 2 2 0 012 2v.654M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <span className="font-bold text-xs sm:text-base">{t('cityDetail.remoteBadge')}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-3 sm:gap-6 text-slate-200 text-[11px] sm:text-sm">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <span className="font-medium">{getCityName()}, {getCountryName()}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{isRemoteHub ? `${cityJobs.length} ${t('cityDetail.remotePositions')}` : `${cityJobs.length} ${t('cityDetail.availablePositions')}`}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ③ Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 sm:mb-12">
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
          <div className="flex flex-col gap-4 sm:gap-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <div className="flex flex-col gap-1 sm:gap-1.5">
                <label className="text-[10px] font-bold uppercase text-slate-400 tracking-widest ml-1">{t('cityDetail.filterDepartment')}</label>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="bg-slate-50 border border-slate-100 rounded-xl px-3 sm:px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all w-full sm:w-auto sm:min-w-[180px]"
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{getDepartmentLabel(dept)}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1 sm:gap-1.5">
                <label className="text-[10px] font-bold uppercase text-slate-400 tracking-widest ml-1">{t('cityDetail.filterType')}</label>
                <div className="flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide">
                  {types.map(type => (
                    <button
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition-all border cursor-pointer whitespace-nowrap shrink-0 ${
                        typeFilter === type
                        ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300 hover:text-blue-600'
                      }`}
                    >
                      {getJobTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between sm:justify-start gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
              <button
                onClick={() => {setDepartmentFilter('All'); setTypeFilter('All');}}
                className="text-xs sm:text-sm font-bold text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                {t('cityDetail.resetFilters')}
              </button>
              <div className="h-6 w-px bg-slate-200 hidden sm:block"></div>
              <div className="text-xs sm:text-sm font-medium text-slate-500">
                {t('cityDetail.showingJobs', { count: cityJobs.length })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ④ Job List - 统一采用列表样式 */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        {cityJobs.length > 0 ? (
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm divide-y divide-slate-50">
            {cityJobs.map(job => (
              <JobRow key={job.id} job={job} onSelect={onSelectJob} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-12 sm:p-24 text-center border border-dashed border-slate-200">
            <div className="max-w-sm mx-auto">
              <div className="relative w-32 h-32 sm:w-48 sm:h-48 mx-auto mb-6 sm:mb-8">
                <div className="absolute inset-0 bg-blue-50 rounded-full animate-pulse"></div>
                <svg className="relative w-full h-full text-slate-200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="100" cy="100" r="60" fill="currentColor" fillOpacity="0.5"/>
                  <path d="M140 140L170 170" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
                  <path d="M80 85L120 115M120 85L80 115" stroke="white" strokeWidth="8" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-3 sm:mb-4 tracking-tight">{t('cityDetail.emptyDesk', { city: city.name })}</h3>
              <p className="text-slate-500 font-medium mb-6 sm:mb-10 leading-relaxed text-sm sm:text-base">
                {t('cityDetail.emptyDeskDesc', { city: city.name })}
              </p>
              <button
                onClick={() => {setDepartmentFilter('All'); setTypeFilter('All');}}
                className="bg-slate-900 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:bg-blue-600 transition-all shadow-xl shadow-slate-200 active:scale-95 cursor-pointer"
              >
                {t('cityDetail.clearFilters')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default CityDetail;
